import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../Contexts/AuthContext";

/**
 * ModuleGuard - High-fidelity route protection.
 * 
 * Checks if the current user handles the required module path or 
 * belongs to the required industry type.
 */
export default function ModuleGuard({ children, requiredPath, requiredIndustry }) {
    const { user, navigation, loading, isAuthenticated } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div style={{ 
                height: "100vh", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                background: "#f9fafb" 
            }}>
                <div className="loader">Verifying Workspace...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    // Superusers and Staff have access to everything in Private Edition
    if (user?.is_superuser || user?.is_staff) {
        return children;
    }

    const orgDetails = user?.organization_details;
    const industryType = orgDetails?.industry_type;

    // 1. Industry Check (Global check for Medical, Education, etc.)
    if (requiredIndustry && industryType !== requiredIndustry) {
        console.warn(`Access Denied: Required industry '${requiredIndustry}', but user is in '${industryType}'`);
        return <Navigate to="/forbidden" replace />;
    }

    // 2. Module/Navigation Check (Granular check for enabled modules)
    if (requiredPath) {
        const hasAccess = navigation.some(module => 
            module.path === requiredPath || location.pathname.startsWith(module.path)
        );

        if (!hasAccess) {
            console.warn(`Access Denied: Module path '${requiredPath}' is not enabled for this organization.`);
            return <Navigate to="/forbidden" replace />;
        }
    }

    return children;
}
